import { useState, useEffect, useRef, useCallback } from 'react';
import TranscriptService from '../../../services/transcript/TranscriptService';

/**
 * useAutoSave Hook
 * 
 * Automatically saves transcript changes after a period of inactivity
 * 
 * @param {Array} blocks - Current transcript blocks
 * @param {string} sessionId - Session ID
 * @param {number} delay - Debounce delay in milliseconds (default 3000ms)
 * @param {boolean} enabled - Whether auto-save is enabled (default true)
 * @returns {Object} Save status and manual save function
 */
const useAutoSave = (blocks, sessionId, delay = 60000, enabled = true) => { // Changed to 60s (1 minute)
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  
  const timeoutRef = useRef(null);
  const previousBlocksRef = useRef(blocks);
  const isMountedRef = useRef(true);
  const savingInProgressRef = useRef(false);

  /**
   * Save blocks to backend
   */
  const saveBlocks = useCallback(
    async (blocksToSave) => {
      // Prevent concurrent saves
      if (savingInProgressRef.current) {
        return;
      }

      if (!sessionId || !blocksToSave || blocksToSave.length === 0) {
        return;
      }

      savingInProgressRef.current = true;
      setIsSaving(true);
      setSaveError(null);

      try {
        const response = await TranscriptService.updateTranscript(sessionId, blocksToSave);
        
        if (!response.success) {
          throw new Error(response.error);
        }

        if (isMountedRef.current) {
          setLastSaved(new Date());
          setIsDirty(false);
          setSaveError(null);
        }
      } catch (error) {
        if (isMountedRef.current) {
          setSaveError(error.message || 'Failed to save transcript');
        }
      } finally {
        savingInProgressRef.current = false;
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [sessionId]
  );

  /**
   * Manual save function
   */
  const manualSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    return saveBlocks(blocks);
  }, [blocks, saveBlocks]);

  /**
   * Manual save with version (creates new version)
   */
  const manualSaveVersion = useCallback(async (description = null) => {
    // Prevent concurrent saves
    if (savingInProgressRef.current) {
      return { success: false, error: 'Save already in progress' };
    }

    if (!sessionId || !blocks || blocks.length === 0) {
      return { success: false, error: 'No content to save' };
    }

    savingInProgressRef.current = true;
    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await TranscriptService.saveVersion(sessionId, blocks, description);

      if (!response.success) {
        throw new Error(response.error);
      }

      if (isMountedRef.current) {
        setLastSaved(new Date());
        setIsDirty(false);
        setSaveError(null);
      }

      return { success: true, versionNumber: response.versionNumber };
    } catch (error) {
      if (isMountedRef.current) {
        setSaveError(error.message || 'Failed to save version');
      }
      return { success: false, error: error.message || 'Failed to save version' };
    } finally {
      savingInProgressRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [sessionId, blocks]);

  /**
   * Check if blocks have changed
   * Treats undefined/null as empty array so missing data does not throw.
   */
  const hasBlocksChanged = useCallback((newBlocks, oldBlocks) => {
    const a = Array.isArray(newBlocks) ? newBlocks : [];
    const b = Array.isArray(oldBlocks) ? oldBlocks : [];
    if (a.length !== b.length) return true;

    return a.some((block, index) => {
      const oldBlock = b[index];
      return (
        block.text !== oldBlock.text ||
        block.speaker !== oldBlock.speaker ||
        block.start !== oldBlock.start ||
        block.end !== oldBlock.end
      );
    });
  }, []);

  /**
   * Auto-save effect
   */
  useEffect(() => {
    if (!enabled || !sessionId) {
      return;
    }

    // Don't start new auto-save if already saving
    if (isSaving) {
      return;
    }

    const normalizedBlocks = Array.isArray(blocks) ? blocks : [];

    // Check if blocks have actually changed
    const changed = hasBlocksChanged(normalizedBlocks, previousBlocksRef.current);
    if (!changed) {
      return;
    }

    // Mark as dirty
    setIsDirty(true);
    previousBlocksRef.current = normalizedBlocks;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save
    timeoutRef.current = setTimeout(() => {
      saveBlocks(blocks);
    }, delay);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, sessionId, delay, enabled, isSaving]); // Added isSaving to prevent concurrent saves

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Save on browser unload
   */
  useEffect(() => {
    if (!enabled || !sessionId) return;

    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        // Attempt synchronous save
        navigator.sendBeacon(
          `/api/sessions/${sessionId}/transcript`,
          JSON.stringify({ segments: blocks })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, sessionId, isDirty, blocks]);

  return {
    isSaving,
    lastSaved,
    saveError,
    isDirty,
    manualSave,
    manualSaveVersion,  // NEW: Creates numbered version
  };
};

export default useAutoSave;


